import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, OTP_STATE } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { User, Phone, Mail, Lock, Loader2, ArrowLeft, MapPin } from 'lucide-react';
import { authService } from '@/services/api';
import { isStrongPassword, isValidPersonName } from '@/lib/accountValidation';

// Country codes for phone input
const COUNTRY_CODES = [
  { code: '963', country: 'Syria', flag: '🇸🇾' },
  { code: '966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '971', country: 'UAE', flag: '🇦🇪' },
  { code: '962', country: 'Jordan', flag: '🇯🇴' },
  { code: '961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '20', country: 'Egypt', flag: '🇪🇬' },
  { code: '964', country: 'Iraq', flag: '🇮🇶' },
  { code: '1', country: 'USA', flag: '🇺🇸' },
];

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const { register, verifyOtp, resendOtp, cancelOtpVerification, otpState, pendingAuth } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('963');
  const [otpCode, setOtpCode] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [resending, setResending] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cityId: '',
    password: '',
    confirmPassword: '',
  });

  // OTP countdown timer
  useEffect(() => {
    let interval;
    if (otpState !== OTP_STATE.NONE && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpState, otpTimer]);

  // Start timer when OTP is needed
  useEffect(() => {
    if (otpState !== OTP_STATE.NONE) {
      setOtpTimer(120); // 2 minutes
    }
  }, [otpState]);

  useEffect(() => {
    const loadCities = async () => {
      setCitiesLoading(true);
      try {
        const response = await authService.getCities();
        const loadedCities = Array.isArray(response.data?.data?.cities)
          ? response.data.data.cities
          : Array.isArray(response.data?.cities)
            ? response.data.cities
            : [];
        setCities(loadedCities.filter((city) => city?.id && city?.name));
      } catch (error) {
        setCities([]);
      } finally {
        setCitiesLoading(false);
      }
    };

    loadCities();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate privacy policy
    if (!acceptPrivacy) {
      toast.error(t('common.error'), {
        description: lang === 'ar' 
          ? 'يرجى الموافقة على سياسة الخصوصية للمتابعة'
          : 'Please accept our privacy policy to continue',
      });
      return;
    }

    if (!formData.cityId) {
      toast.error(t('common.error'), {
        description: lang === 'ar' ? 'يرجى اختيار المدينة' : 'Please choose city',
      });
      return;
    }

    if (!isValidPersonName(formData.name)) {
      toast.error(t('common.error'), {
        description: lang === 'ar'
          ? 'الاسم يجب أن يكون من حرفين إلى 100 حرف، من دون أرقام أو رموز.'
          : 'Name must be 2–100 letters and spaces, without numbers or symbols.',
      });
      return;
    }

    if (!isStrongPassword(formData.password)) {
      toast.error(t('common.error'), {
        description: lang === 'ar'
          ? 'كلمة المرور يجب أن تتضمن 8 محارف وحرفًا كبيرًا وصغيرًا ورقمًا ورمزًا.'
          : 'Password needs 8 characters with uppercase, lowercase, number, and symbol.',
      });
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('common.error'), {
        description: lang === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match',
      });
      return;
    }

    setLoading(true);
    try {
      const result = await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        countryCode: countryCode,
        cityId: formData.cityId,
      });
      
      if (result.success || result.needsOtp) {
        toast.info(lang === 'ar' ? 'التحقق مطلوب' : 'Verification Required', {
          description: lang === 'ar' 
            ? 'تم إرسال رمز التحقق إلى هاتفك/بريدك الإلكتروني'
            : 'OTP code has been sent to your phone/email',
        });
      } else {
        toast.error(t('common.error'), {
          description: result.error || 'Registration failed',
        });
      }
    } catch (error) {
      toast.error(t('common.error'), {
        description: lang === 'ar' ? 'حدث خطأ غير متوقع' : 'An unexpected error occurred',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length < 4) {
      toast.error(t('common.error'), {
        description: lang === 'ar' ? 'الرجاء إدخال رمز صحيح' : 'Please enter a valid OTP code',
      });
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOtp(otpCode);
      
      if (result.success) {
        toast.success(t('common.success'), {
          description: lang === 'ar' ? 'تم التسجيل بنجاح!' : 'Registration successful!',
        });
        // Redirect to patient profile creation (same as Flutter)
        navigate('/profile');
      } else {
        toast.error(t('common.error'), {
          description: result.error || (lang === 'ar' ? 'رمز خاطئ' : 'Wrong OTP code'),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      const result = await resendOtp();
      if (result.success) {
        setOtpTimer(120);
        toast.success(t('common.success'), {
          description: lang === 'ar' ? 'تم إعادة إرسال الرمز' : 'OTP code resent',
        });
      } else {
        toast.error(t('common.error'), {
          description: result.error,
        });
      }
    } finally {
      setResending(false);
    }
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // OTP Verification View
  if (otpState === OTP_STATE.REGISTER_REQUIRED) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4" data-testid="otp-page">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <img src="/images/logo.png" alt="Dactrha" className="h-12 w-12 rounded-lg object-contain" />
              <span className="text-2xl font-bold text-slate-900 dark:text-white">{t('common.appName')}</span>
            </Link>
          </div>

          <Card className="dark:bg-slate-800 border-0 shadow-xl">
            <CardHeader className="text-center pb-2 relative">
              <Button 
                variant="ghost" 
                size="sm" 
                className="absolute left-4 top-4"
                onClick={cancelOtpVerification}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('common.back')}
              </Button>
              <CardTitle className="text-2xl">
                {lang === 'ar' ? 'التحقق من الهاتف' : 'Phone Verification'}
              </CardTitle>
              <CardDescription>
                {lang === 'ar' 
                  ? 'مرحباً بك في تطبيق دكترها'
                  : 'Welcome to Dactrha application'
                }
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-4 space-y-6">
              <p className="text-center text-slate-600 dark:text-slate-400">
                {countryCode === '963' && formData.phone
                  ? (lang === 'ar' 
                      ? 'تم إرسال رمز التحقق إلى هاتفك، يرجى التحقق وإدخاله أدناه'
                      : 'We sent the OTP code to your phone, please check it and enter below')
                  : (lang === 'ar'
                      ? 'تم إرسال رمز التحقق إلى بريدك الإلكتروني، يرجى التحقق وإدخاله أدناه'
                      : 'We sent the OTP code to your email, please check it and enter below')
                }
              </p>

              <div className="space-y-2">
                <Label>{lang === 'ar' ? 'رمز التحقق' : 'OTP Code'}</Label>
                <Input
                  type="text"
                  placeholder="- - - - - -"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>

              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700" 
                onClick={handleVerifyOtp}
                disabled={loading || otpCode.length < 4}
                data-testid="verify-otp-button"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.loading')}</>
                ) : (
                  lang === 'ar' ? 'تحقق' : 'Verify'
                )}
              </Button>

              <div className="text-center">
                {otpTimer > 0 ? (
                  <p className="text-slate-500 dark:text-slate-400">{formatTimer(otpTimer)}</p>
                ) : resending ? (
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                ) : (
                  <Button variant="link" onClick={handleResendOtp} className="text-teal-600">
                    {lang === 'ar' ? 'إعادة إرسال رمز التحقق' : 'Resend the OTP Code Again'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Registration Form View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/images/logo.png" alt="Dactrha" className="h-12 w-12 rounded-lg object-contain" />
            <span className="text-2xl font-bold text-slate-900 dark:text-white">{t('common.appName')}</span>
          </Link>
        </div>

        <Card className="dark:bg-slate-800 border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">{t('auth.registerTitle')}</CardTitle>
            <CardDescription>{t('auth.registerSubtitle')}</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.fullName')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={lang === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <Label htmlFor="phone">{lang === 'ar' ? 'رقم الهاتف' : 'Phone Number'}</Label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-[110px]" data-testid="country-code-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} +{c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="912345678"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label htmlFor="cityId">{lang === 'ar' ? 'المدينة' : 'City'}</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                  <Select
                    value={formData.cityId}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, cityId: value }))}
                    required
                  >
                    <SelectTrigger className="pl-10" data-testid="city-select">
                      <SelectValue placeholder={citiesLoading ? (lang === 'ar' ? 'جاري التحميل' : 'Loading') : (lang === 'ar' ? 'اختر المدينة' : 'Choose city')} />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((city) => (
                        <SelectItem key={city.id} value={String(city.id)}>
                          {city.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {/* Privacy Policy */}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="acceptPrivacy"
                  checked={acceptPrivacy}
                  onCheckedChange={setAcceptPrivacy}
                  data-testid="privacy-checkbox"
                />
                <Label htmlFor="acceptPrivacy" className="text-sm font-normal cursor-pointer leading-relaxed">
                  {lang === 'ar' 
                    ? 'أوافق على سياسة الخصوصية وشروط الاستخدام'
                    : 'I agree to the Privacy Policy and Terms of Service'
                  }
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700" 
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.loading')}</>
                ) : (
                  t('common.register')
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pt-0">
            <p className="text-center text-sm text-slate-600 dark:text-slate-400">
              {t('auth.haveAccount')}{' '}
              <Link to="/login" className="font-medium text-teal-600 hover:underline" data-testid="login-link">
                {t('common.login')}
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
